import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute } from '@angular/router';

import { PortalApiService } from '../../portal-api.service';
import { PortalAuthService } from '../../portal-auth.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { portalDate } from '../../shared/portal-format';
import { ToBeConfirmedModalComponent } from '../../shared/to-be-confirmed-modal/to-be-confirmed-modal.component';
import { RejectModalComponent } from '../../shared/reject-modal/reject-modal.component';
import { PortalDocumentOpener } from '../../shared/portal-document';

type QuoteFilter = 'all' | 'pending_review' | 'reviewed' | 'to_be_confirmed' | 'accepted' | 'declined';

@Component({
	selector: 'portal-quotes',
	templateUrl: './quotes.component.html'
})
export class QuotesComponent implements OnInit, OnDestroy {

	// Filter tabs, in display order. Counts are keyed to match.
	readonly tabs: { key: QuoteFilter; label: string }[] = [
		{ key: 'all', label: 'All' },
		{ key: 'pending_review', label: 'Pending Review' },
		{ key: 'reviewed', label: 'Reviewed' },
		{ key: 'to_be_confirmed', label: 'To Be Confirmed' },
		{ key: 'accepted', label: 'Accepted' },
		{ key: 'declined', label: 'Declined' }
	];

	@ViewChild('tbcModal') tbcModal!: ToBeConfirmedModalComponent;
	@ViewChild('rejectModal') rejectModal!: RejectModalComponent;

	// Opens documents in new tabs and reloads the list when the customer returns.
	private docs!: PortalDocumentOpener;
	private sub?: Subscription;

	projectId!: string;
	loading = true;
	error = '';

	quotes: any[] = [];
	sections: string[] = [];
	counts: any = { all: 0, pending_review: 0, reviewed: 0, to_be_confirmed: 0, accepted: 0, declined: 0 };

	filter: QuoteFilter = 'all';
	search = '';

	constructor(
		private route: ActivatedRoute,
		private api: PortalApiService,
		private auth: PortalAuthService,
		public preview: PortalPreviewService
	) {}

	get filtered(): any[] {
		const q = this.search.trim().toLowerCase();
		return this.quotes.filter(row => {
			if (this.filter !== 'all' && row.status !== this.filter) return false;
			if (!q) return true;
			return (`${row.quote_no} ${row.title} ${row.description}`).toLowerCase().includes(q);
		});
	}

	ngOnInit(): void {
		// In a preview the document opens by its own URL (the /preview variant) rather than through
		// the portal's document page, which would have no session in an unframed tab.
		this.docs = new PortalDocumentOpener('', () => this.load(),
			this.preview.ready
				? (quoteId, mode, target, done) => this.api.quote.document(
					quoteId, target, mode,
					(res: any) => done(res?.url || null),
					() => done(null))
				: null);

		// Subscribed rather than read once from the snapshot: Angular reuses this
		// component when only the :id changes, so switching project (from the activity
		// feed, or with the browser's back button) would otherwise leave the previous
		// project's content on screen. Resetting here rather than inside load() keeps
		// the opener's refresh-on-return silent.
		this.sub = this.route.params.subscribe(params => {
			this.projectId = params['id'] || '';
			this.docs.projectId = this.projectId;
			this.loading = true;
			this.error = '';
			this.load();
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
		this.docs?.destroy();
	}

	load(): void {
		this.api.project.quotes(this.projectId, (res: any) => {
			this.quotes = res?.quotes || [];
			this.sections = res?.sections || [];
			this.counts = res?.counts || this.counts;
			this.loading = false;
		}, (err: any) => {
			this.loading = false;
			if (err?.status === 401) { this.auth.goToLogin(); return; }
			this.error = err?.message || 'Could not load quotes.';
		});
	}

	// Percentage of a quote's own payment breakdown (paid / to_pay_now / upcoming),
	// for the inline payment summary shown on accepted rows.
	payPct(q: any, key: 'paid' | 'to_pay_now' | 'upcoming'): number {
		const total = q?.payment?.total || 0;
		if (!total) return 0;
		return Math.round(Math.min(100, Math.max(0, ((q.payment[key] || 0) / total) * 100)));
	}

	// Every not-yet-decided state — controls whether the accept / reject actions show.
	isPending(q: any): boolean {
		return q.status === 'pending_review' || q.status === 'reviewed' || q.status === 'to_be_confirmed';
	}

	// A quote can be flagged "To be confirmed" while it's still awaiting a decision
	// (Pending Review or Reviewed) — the acknowledgement step before acceptance.
	canConfirm(q: any): boolean {
		return q.status === 'pending_review' || q.status === 'reviewed';
	}

	// Open the reason picker; the modal records the note + flag on submit.
	openToBeConfirmed(q: any): void {
		if (!this.canConfirm(q)) return;
		if (this.preview.ready) return;   // the button is disabled; belt-and-braces
		this.tbcModal.open(q);
	}

	// After the modal confirms, move the quote into the To Be Confirmed bucket.
	onToBeConfirmed(e: any): void {
		const q = e?.quote;
		if (!q) return;
		const from = q.status;
		if (this.counts[from] > 0) this.counts[from]--;
		this.counts.to_be_confirmed++;
		q.status = 'to_be_confirmed';
		q.to_be_confirmed = true;
	}

	// Open the reject reason picker; the modal records the note + cancels on submit.
	openReject(q: any): void {
		if (!this.isPending(q)) return;
		if (this.preview.ready) return;   // the button is disabled; belt-and-braces
		this.rejectModal.open(q);
	}

	// After the modal rejects, move the quote into the Declined bucket.
	onRejected(e: any): void {
		const q = e?.quote;
		if (!q) return;
		const from = q.status;
		if (this.counts[from] > 0) this.counts[from]--;
		this.counts.declined++;
		q.status = 'declined';
	}


	// Open the quote's published web proposal on the portal's own document page, in a
	// new tab. That page frames the proposal, so the customer stays on a portal URL.
	openProposal(q: any): void {
		if (q?.can_view_proposal) this.docs.open(q.id);
	}

	// Same, with the sign & accept modal auto-opened on the proposal.
	acceptQuote(q: any): void {
		if (q?.can_accept) this.docs.open(q.id, 'accept');
	}

	// Same, with the add-comment modal auto-opened on the proposal.
	commentQuote(q: any): void {
		if (q?.can_comment) this.docs.open(q.id, 'comment');
	}

	// Customer-facing date label per status.
	dateLabel(q: any): { text: string; cls: string } {
		if (q.status === 'accepted') {
			return { text: q.accepted_date ? `Accepted on ${portalDate(q.accepted_date)}` : 'Accepted', cls: 'qr-date-accepted' };
		}
		if (q.status === 'declined') {
			return { text: '', cls: 'qr-date-overdue' };
		}
		if (q.expiry_date) {
			const overdue = new Date(q.expiry_date) < new Date();
			return overdue
				? { text: `Overdue ${portalDate(q.expiry_date)}`, cls: 'qr-date-overdue' }
				: { text: `Valid until ${portalDate(q.expiry_date)}`, cls: 'qr-date-pending' };
		}
		return { text: '', cls: 'qr-date-pending' };
	}

	badgeClass(status: string): string {
		switch (status) {
			case 'accepted': return 'badge-success';          // green
			case 'declined': return 'badge-danger';           // purple
			case 'to_be_confirmed': return 'badge-secondary'; // grey
			// Reviewed shares Pending Review's amber badge — no separate colour.
			default: return 'badge-warning';                  // pending_review / reviewed
		}
	}

	badgeLabel(status: string): string {
		switch (status) {
			case 'accepted': return 'Accepted';
			case 'declined': return 'Declined';
			case 'to_be_confirmed': return 'To Be Confirmed';
			case 'reviewed': return 'Reviewed';
			default: return 'Pending Review';
		}
	}

}
